import React, { useEffect, useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Input,
  Text,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import CButton from './CButton';
import MemberGroupDropdown from './MemberGroupDropdown';

const CMenu = ({
  label,
  fileType,
  onRangeSelect,
  onCustomDateSubmit,
  onMemberGroupSubmit,
  isMemberGroupMenu = false,
  useUnifiedSubmit = false,
  onUnifiedSubmit,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [accordionIndex, setAccordionIndex] = useState(null);
  const [memberGroupId, setMemberGroupId] = useState(null);
  const [customDates, setCustomDates] = useState({ startDate: '', endDate: '' });
  const [selectedRange, setSelectedRange] = useState(null);
  const [menuIsNotOpen, setMenuIsNotOpen] = useState(true)

  const menuBg = useColorModeValue('white', 'navy.800');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );

  const handleCustomSubmit = () => {
    if (customDates.startDate && customDates.endDate) {
      onCustomDateSubmit(fileType, customDates.startDate, customDates.endDate);
      resetState();
    }
  };

  const handleMemberGroupSubmit = () => {
    if (memberGroupId) {
      onMemberGroupSubmit(fileType, memberGroupId.value);
      resetState();
    }
  };

  useEffect(() => {
    setMenuIsNotOpen(!isOpen)
  }, [isOpen])

  useEffect(() => {
    if (menuIsNotOpen) {
      resetState({ clearAll: true });
    }
  }, [menuIsNotOpen])

  const resetState = ({
    clearAll = false,
    clearDates = true,
    clearGroup = true,
    clearRange = true,
  } = {}) => {
    if (clearDates && (!useUnifiedSubmit || clearAll)) setCustomDates({ startDate: '', endDate: '' });
    if (clearGroup) setMemberGroupId(null);
    if (clearRange && (!useUnifiedSubmit || clearAll)) setSelectedRange(null);
    setAccordionIndex(null);
    onClose();
  };

  const dateRanges = isMemberGroupMenu
    ? ['Today', 'This Week', 'This Month', 'This Year']
    : ['All', 'This Week', 'This Month', 'This Year'];
  const rangeValues = isMemberGroupMenu
    ? ['today', 'week', 'month', 'year']
    : ['all', 'week', 'month', 'year'];

  return (
    <Menu
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={() => {
        if (!useUnifiedSubmit) {
          resetState({ clearAll: true });
        } else {
          setAccordionIndex(null);
          onClose();
        }
      }}
      closeOnSelect={false}
    >
      <MenuButton onClick={onOpen} p="0px" textUnderlineOffset="initial" type="button">
        <CButton>{label}</CButton>
      </MenuButton>

      <MenuList boxShadow={shadow} p="0px" mt="10px" borderRadius="20px" bg={menuBg} border="none">
        <Flex flexDirection="column" p="10px">
          {/* Preset Date Ranges */}
          {dateRanges.map((range, index) => (
            <MenuItem
              key={range}
              borderRadius="8px"
              px="14px"
              backgroundColor={selectedRange === rangeValues[index] ? '#fa8f03' : "white"}
              color={selectedRange === rangeValues[index] ? 'white' : "black"}
              onClick={() => {
                if (useUnifiedSubmit) {
                  setSelectedRange(rangeValues[index]);
                  setCustomDates({ startDate: '', endDate: '' });
                } else {
                  onRangeSelect(fileType, rangeValues[index]);
                  resetState();
                }
              }}
            >
              <Text fontSize="sm">{range}</Text>
            </MenuItem>
          ))}

          {/* Custom Date & Member Group Selection */}
          <Accordion allowToggle index={accordionIndex} onChange={(index) => setAccordionIndex(index)}>
            {/* Custom Date Selection */}
            <AccordionItem border="none" my="5px">
              <AccordionButton px="14px" py="0" borderRadius="8px" width="100%">
                <Box flex="1" textAlign="left">
                  <Text fontSize="sm">Custom</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb="4">
                <Flex flexDirection="column" gap="10px">
                  <Input
                    placeholder="Start Date"
                    size="sm"
                    borderRadius="8px"
                    type="date"
                    value={customDates.startDate}
                    onChange={(e) => {
                      setCustomDates((prev) => ({ ...prev, startDate: e.target.value }))
                      setSelectedRange(null)
                    }}
                  />
                  <Input
                    placeholder="End Date"
                    size="sm"
                    borderRadius="8px"
                    type="date"
                    value={customDates.endDate}
                    onChange={(e) => {
                      setCustomDates((prev) => ({ ...prev, endDate: e.target.value }))
                      setSelectedRange(null)
                    }}
                  />
                  {!useUnifiedSubmit && (
                    <CButton
                      borderRadius="8px"
                      onClick={handleCustomSubmit}
                      isDisabled={!customDates.startDate || !customDates.endDate}
                    >
                      Submit
                    </CButton>
                  )}
                </Flex>
              </AccordionPanel>
            </AccordionItem>

            {/* Member Group Selection (Conditional) */}
            {isMemberGroupMenu && (
              <AccordionItem border="none">
                <AccordionButton px="14px" py="0" borderRadius="8px" width="100%">
                  <Box flex="1" textAlign="left">
                    <Text fontSize="sm">Member Group</Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb="4">
                  <Flex flexDirection="column" gap="10px">
                    <MemberGroupDropdown
                      label=""
                      value={memberGroupId}
                      onChange={(id) => {
                        setMemberGroupId(id);
                        setAccordionIndex(1);
                        onOpen();
                      }}
                      multiSelect={false}
                    />
                    {!useUnifiedSubmit && (
                      <CButton
                        borderRadius="8px"
                        onClick={handleMemberGroupSubmit}
                        isDisabled={!memberGroupId}
                      >
                        Submit
                      </CButton>
                    )}
                  </Flex>
                </AccordionPanel>
              </AccordionItem>
            )}
          </Accordion>

          {useUnifiedSubmit && (
            <Box mt="10px">
              <CButton
                borderRadius="8px"
                isDisabled={
                  (!customDates.startDate || !customDates.endDate) &&
                  !selectedRange &&
                  !(isMemberGroupMenu && memberGroupId)
                }
                onClick={() => {
                  if (onUnifiedSubmit) {
                    const hasCustomDates = customDates.startDate && customDates.endDate;
                    const hasGroup = isMemberGroupMenu && memberGroupId;
                    const hasQuickRange = selectedRange;

                    let filter;

                    if (hasCustomDates) {
                      filter = 'custom';
                    } else if (hasQuickRange) {
                      filter = selectedRange;
                    } else if (hasQuickRange && hasGroup) {
                      filter = selectedRange;
                    } else if (hasGroup) {
                      filter = 'memberGroup';
                    }

                    onUnifiedSubmit({
                      fileType,
                      filter,
                      startDate: hasCustomDates ? customDates.startDate : null,
                      endDate: hasCustomDates ? customDates.endDate : null,
                      memberGroupId: hasGroup ? memberGroupId.value : null,
                    });
                  }

                  resetState({ clearAll: true });
                }}
              >
                Submit
              </CButton>
            </Box>
          )}
        </Flex>
      </MenuList>
    </Menu>
  );
};

export default CMenu;
